import {
  Row, Col, Flex, Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useRecoilState } from 'recoil';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { userAtom } from '../../../recoil/atoms';
import { AxiosService } from '../../../utils/axiosService';
import AccountDetailCard, { AccountDetailT } from '../components/AccountDetailCard';
import ActivityLogList from '../components/ActivityLogList';
import CreditSummaryList from '../components/CreditSummaryList';
import CustomerDetailCard, { CustomerDetailT } from '../components/CustomerDetailCard';
import MemberDetailCard, { MemberDetailT } from '../components/MemberDetailCard';
import NomineeList from '../components/NomineeList';
import PointList from '../components/PointList';
import PrimaryList from '../components/PrimaryList';
import TransactionList from '../components/TransactionList';
import QuestionnaireList from '../components/questionnaire/QuestionnaireList';
import NonAppVoucherList from '../components/voucher/NonAppVoucherList';
import VoucherList from '../components/voucher/VoucherList';
import usePermission from '../../../hooks/usePermission';
import { PermissionEnum } from '../../../enums/permission';
import AlumniDetailCard, { AlumniDetailT } from '../components/AlumniDetailCard';
import CustomerJobList from '../components/CustomerJobList';

const mockupData = [
  {
    id: 1,
    displayName: 'Somsak Ying',
    email: 'somsak_321@Mail.com',
    memberStatus: 'Non Club card member',
    createdAt: '',
    isActive: true,
  },
  {
    id: 2,
    displayName: 'Somchai',
    email: 'somchai_123@Mail.com',
    memberStatus: 'Club card member',
    createdAt: '',
    isActive: false,
  },
];
const CustomerDetail: React.FC = () => {
  const [user, setUser] = useRecoilState(userAtom);
  const { id: customerId } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [customerDetail, setCustomerDetail] = useState<CustomerDetailT>({
    imageURL: '',
    email: '',
    isActive: false,
    displayName: '',
    latestLoginAt: '',
    createdAt: '',
    updatedAt: '',
    isSso: false,
  });

  const [accountDetail, setAccountDetail] = useState<AccountDetailT>({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    dateOfBirth: '',
    gender: '',
    identificationNumber: '',
    cardExpiryDate: '',
    height: 0,
    weight: 0,
  });

  const [memberDetail, setMemberDetail] = useState<MemberDetailT>({
    creditBalance: 0,
    validUntilAt: '',
    group: '',
    membershipProfileName: '',
    points: 0,
    pointExpired: 0,
    pointExpiredAt: '',
  });
  const [alumniDetail, setAlumniDetail] = useState<AlumniDetailT>({
    faculty: null,
    graduationYear: null,
    batch: null,
    studentId: null,
    preferredName: null,
    jobTitle: null,
    companyName: null,
    companyNameTh: null,
    website: null,
    taxId: null,
    mapUrl: null,
    companyLogoURL: null,
    homeAddress: null,
    homeAddressLine: null,
    homeSubDistrict: null,
    homeDistrict: null,
    homeProvince: null,
    homePostalCode: null,
    workAddress: null,
    workAddressLine: null,
    workSubDistrict: null,
    workDistrict: null,
    workProvince: null,
    workPostalCode: null,
    isDirectoryVisible: true,
    alumniStatus: 'pending',
    userType: 'alumni',
    isOpenToWork: false,
    industry: null,
    skills: null,
    cvURL: null,
    cvFileName: null,
  });
  const [isSso, setIsSso] = useState<boolean>(false);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAccountActive = customerDetail.isActive === true;

  const [nomineeList, setNomineeList] = useState([]);
  const [primaryList, setPrimaryList] = useState([]);
  const path: PathT[] = [
    {
      label: 'Customer Account',
      value: '/customer',
    },
    {
      label: `Account : ${customerDetail.email}`,
    },
  ];

  const { service: getCustomerDetailService } = useServices(ServiceNameEnum.customerMgmt.detail);

  const loadCustomerDetail = async (): Promise<void> => {
    if (customerId) {
      const { data, error } = await getCustomerDetailService.get({ params: { id: customerId } });
      if (data) {
        setCustomerDetail(data);
        setAccountDetail(data);
        if (data?.customerMember) {
          const pointWallet = data.wallets.find((item: any) => item.productType === 'point');
          setMemberDetail({
            ...data.customerMember,
            points: pointWallet.balance,
          });
        }
        if (data?.customerNominee) {
          setNomineeList(data.customerNominee);
        }
        if (data?.customerPrimary) {
          setPrimaryList(data.customerPrimary);
        }
        if (data.accessMethods) {
          setIsSso(data.accessMethods[0].id !== 1);
        }
        setAlumniDetail({
          faculty: data.faculty || null,
          graduationYear: data.graduationYear || null,
          batch: data.batch || null,
          studentId: data.studentId || null,
          preferredName: data.preferredName || null,
          jobTitle: data.jobTitle || null,
          companyName: data.companyName || null,
          companyNameTh: data.companyNameTh || null,
          website: data.website || null,
          taxId: data.taxId || null,
          mapUrl: data.mapUrl || null,
          companyLogoURL: data.companyLogoURL || null,
          homeAddress: data.homeAddress || null,
          homeAddressLine: data.homeAddressLine || null,
          homeSubDistrict: data.homeSubDistrict || null,
          homeDistrict: data.homeDistrict || null,
          homeProvince: data.homeProvince || null,
          homePostalCode: data.homePostalCode || null,
          workAddress: data.workAddress || null,
          workAddressLine: data.workAddressLine || null,
          workSubDistrict: data.workSubDistrict || null,
          workDistrict: data.workDistrict || null,
          workProvince: data.workProvince || null,
          workPostalCode: data.workPostalCode || null,
          isDirectoryVisible: data.isDirectoryVisible ?? true,
          alumniStatus: data.alumniStatus || 'pending',
          userType: data.userType || 'alumni',
          isOpenToWork: data.isOpenToWork ?? false,
          industry: data.industry || null,
          skills: data.skills || null,
          cvURL: data.cvURL || null,
          cvFileName: data.cvFileName || null,
        });
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        navigate(RouteNameEnum.customer.index);
        toast.error(errorResponse?.message, { toastId: 'allToast' });
      }
    }
  };

  useEffect(() => {
    loadCustomerDetail();
  }, []);

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Typography.Title level={4} style={{ fontWeight: 700, marginBottom: '16px' }}>Customer account details</Typography.Title>
      { checkPermission([PermissionEnum.code.customerViewDetail])
      && (
      <>
        <CustomerDetailCard
          customerDetailCard={{
            ...customerDetail,
            isSso,
          }}
          refetchData={loadCustomerDetail}
        />
        <Row gutter={24} style={{ marginTop: 24 }}>
          <Col md={12} xs={24}>
            <AccountDetailCard accountDetail={accountDetail} />
          </Col>
          <Col md={12} xs={24}>
            <AlumniDetailCard alumniDetail={alumniDetail} refetchData={loadCustomerDetail} />
          </Col>
        </Row>
      </>
      )}
      <Flex vertical gap={24} style={{ marginTop: 24 }}>
        {/* { checkPermission([PermissionEnum.code.customerViewVoucherList])
        && <VoucherList />}
        { checkPermission([PermissionEnum.code.customerViewNonApp])
        && <NonAppVoucherList />}
        { checkPermission([PermissionEnum.code.customerViewQuestionnaire])
        && <QuestionnaireList />}
        { checkPermission([PermissionEnum.code.customerViewPoint])
        && <PointList />}
        { checkPermission([PermissionEnum.code.customerViewCreditSummary])
        && <CreditSummaryList />}
        { checkPermission([PermissionEnum.code.customerViewTransaction])
        && <TransactionList />}
        { checkPermission([PermissionEnum.code.customerViewNominee])
        && <NomineeList data={nomineeList} />}
        { checkPermission([PermissionEnum.code.customerViewPrimary])
        && <PrimaryList data={primaryList} />} */}
        { checkPermission([PermissionEnum.code.customerViewActivityLog])
        && <ActivityLogList />}
        <CustomerJobList />
      </Flex>
    </div>
  );
};

export default CustomerDetail;
